import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Building, Plus, Trash2, Pause, Play, Copy, Users, Fingerprint, Tv } from "lucide-react";
import { useAuth } from "@/lib/auth";

const PLANS = ["starter", "pro", "enterprise", "platform"];

export default function PlatformTenants() {
  const { isSuperAdmin, setActiveTenant } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [created, setCreated] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/platform/tenants");
      setTenants(r.data.tenants || []);
    } catch (e) {
      toast.error("Failed to load tenants (super-admin required)");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function toggleStatus(t) {
    const newStatus = t.status === "active" ? "suspended" : "active";
    try {
      await api.patch(`/platform/tenants/${t.id}`, { status: newStatus });
      toast.success(`${t.name} → ${newStatus}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  }

  async function del(t) {
    if (!window.confirm(`Delete tenant "${t.name}"? This wipes ${t.user_count} users, ${t.investigation_count} cases, ${t.kiosk_count} kiosks. Cannot be undone.`)) return;
    try {
      await api.delete(`/platform/tenants/${t.id}`);
      toast.success(`Tenant ${t.name} deleted`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  }

  if (!isSuperAdmin()) {
    return (
      <div className="min-h-screen bg-[#060606] text-white flex items-center justify-center p-8" data-testid="platform-forbidden">
        <div className="max-w-md text-center">
          <Building className="w-10 h-10 text-[#FFC542] mx-auto mb-3" />
          <div className="font-display font-black text-2xl mb-2">Super-admin only</div>
          <p className="text-sm text-[#8E9196] font-mono">
            This page is reserved for platform operators. Sign in with a super-admin account.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#060606] text-white p-8" data-testid="platform-tenants-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Building className="w-5 h-5 text-[#B57BFF]" strokeWidth={1.75} />
          <h1 className="font-display font-black text-3xl tracking-tight">TENANTS</h1>
        </div>
        <p className="text-sm text-[#8E9196] max-w-2xl mb-6">
          Every customer company that runs on ForensiQ. Each tenant is fully isolated — users, cases, kiosks, and intel feeds live only inside their tenant. Suspend a tenant to instantly force-logout every user; delete to wipe their entire footprint.
        </p>

        <div className="flex justify-end mb-4">
          <button
            onClick={() => setShowCreate(true)}
            data-testid="tenant-new-btn"
            className="flex items-center gap-2 px-4 py-2 bg-[#B57BFF] text-black text-[10px] font-mono uppercase tracking-widest rounded-sm hover:bg-white transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            New tenant
          </button>
        </div>

        {/* Table */}
        <div className="border border-white/10 rounded-sm bg-[#0a0a0a]" data-testid="tenant-list">
          <div className="grid grid-cols-[1.5fr_100px_110px_180px_100px_120px] px-4 py-2 border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">
            <div>Name / Slug</div>
            <div>Plan</div>
            <div>Status</div>
            <div>Seats (used/cap)</div>
            <div>Created</div>
            <div></div>
          </div>
          {loading && <div className="p-6 text-xs text-[#52525B] font-mono">Loading…</div>}
          {!loading && tenants.length === 0 && (
            <div className="p-6 text-xs text-[#52525B] font-mono">No tenants yet.</div>
          )}
          {tenants.map((t) => (
            <div key={t.id}
                 className="grid grid-cols-[1.5fr_100px_110px_180px_100px_120px] items-center px-4 py-3 border-b border-white/[0.04] last:border-0 text-sm"
                 data-testid={`tenant-row-${t.id}`}>
              <div>
                <div className="font-medium">{t.name}</div>
                <div className="text-[10px] font-mono text-[#8E9196]">{t.slug}</div>
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#B57BFF]">{t.plan}</div>
              <div>
                <span className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest ${
                  t.status === "active" ? "text-[#00F0FF]" : "text-[#FFC542]"
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${t.status === "active" ? "bg-[#00F0FF]" : "bg-[#FFC542]"}`} />
                  {t.status}
                </span>
              </div>
              <div className="flex items-center gap-3 text-[10px] font-mono text-[#8E9196]" data-testid={`tenant-usage-${t.id}`}>
                {["admin", "analyst", "viewer"].map((r) => {
                  const cap = t.user_limits?.[r];
                  const used = t.role_usage?.[r] ?? 0;
                  const isUnlimited = cap === -1 || cap == null;
                  const isFull = !isUnlimited && cap > 0 && used >= cap;
                  return (
                    <span key={r} title={`${r}: ${used}${isUnlimited ? " (unlimited)" : `/${cap}`}`}
                          className={isFull ? "text-[#FF6B35]" : cap === 0 ? "text-[#52525B]" : ""}>
                      <span className="uppercase text-[#B57BFF]">{r[0]}</span>{used}{isUnlimited ? "" : `/${cap}`}
                    </span>
                  );
                })}
              </div>
              <div className="text-[10px] font-mono text-[#8E9196]">
                {t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}
              </div>
              <div className="flex items-center justify-end gap-1">
                <button
                  onClick={() => {
                    const raw = window.prompt(
                      `Seat caps for ${t.name} (comma-separated: admin,analyst,viewer). -1 = unlimited, 0 = blocked.`,
                      `${t.user_limits?.admin ?? 3},${t.user_limits?.analyst ?? 25},${t.user_limits?.viewer ?? 100}`,
                    );
                    if (!raw) return;
                    const parts = raw.split(",").map((s) => parseInt(s.trim(), 10));
                    if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) {
                      toast.error("Enter three numbers separated by commas");
                      return;
                    }
                    api.patch(`/platform/tenants/${t.id}`, {
                      user_limits: { admin: parts[0], analyst: parts[1], viewer: parts[2] },
                    })
                      .then(() => { toast.success("Seat caps updated"); load(); })
                      .catch((e) => toast.error(e.response?.data?.detail || "Update failed"));
                  }}
                  data-testid={`tenant-limits-${t.id}`}
                  title="Edit seat caps (admin/analyst/viewer)"
                  className="p-1.5 border border-white/10 hover:border-[#B57BFF] hover:text-[#B57BFF] rounded-sm text-[#8E9196] transition-colors"
                >
                  <Users className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => { setActiveTenant(t.id); }}
                  data-testid={`tenant-impersonate-${t.id}`}
                  title="Switch active tenant & reload"
                  className="p-1.5 border border-white/10 hover:border-[#00F0FF] hover:text-[#00F0FF] rounded-sm text-[#8E9196] transition-colors"
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => toggleStatus(t)}
                  data-testid={`tenant-toggle-${t.id}`}
                  title={t.status === "active" ? "Suspend tenant" : "Reactivate tenant"}
                  className="p-1.5 border border-white/10 hover:border-[#FFC542] hover:text-[#FFC542] rounded-sm text-[#8E9196] transition-colors"
                >
                  {t.status === "active" ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                </button>
                <button
                  onClick={() => del(t)}
                  data-testid={`tenant-delete-${t.id}`}
                  title="Delete tenant"
                  className="p-1.5 border border-white/10 hover:border-[#FF2E63] hover:text-[#FF2E63] rounded-sm text-[#8E9196] transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showCreate && (
        <NewTenantModal
          onCancel={() => setShowCreate(false)}
          onCreated={(resp) => { setShowCreate(false); setCreated(resp); load(); }}
        />
      )}
      {created && (
        <CreatedModal data={created} onClose={() => setCreated(null)} />
      )}
    </div>
  );
}

function NewTenantModal({ onCancel, onCreated }) {
  const [form, setForm] = useState({
    name: "", plan: "starter",
    admin_email: "", admin_name: "", admin_password: "",
    user_limits: { admin: 3, analyst: 25, viewer: 100 },
  });
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!form.name.trim() || !form.admin_email.trim() || !form.admin_name.trim()) {
      toast.error("Tenant name, admin email, and admin name are required");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/platform/tenants", {
        name: form.name.trim(),
        plan: form.plan,
        admin_email: form.admin_email.trim(),
        admin_name: form.admin_name.trim(),
        admin_password: form.admin_password || undefined,
        user_limits: form.user_limits,
      });
      toast.success(`${r.data.tenant.name} created`);
      onCreated(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-8" onClick={onCancel}>
      <div className="bg-[#0a0a0a] border border-[#B57BFF]/60 rounded-sm p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="tenant-new-modal">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#B57BFF] mb-1">Onboard new customer</div>
        <div className="font-display font-black text-2xl mb-4">New tenant</div>
        <div className="space-y-3">
          <Field label="Company name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="new-tenant-name" placeholder="Acme Corp" />
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#52525B] mb-1">Plan</div>
            <div className="flex gap-1">
              {PLANS.map((p) => (
                <button key={p} onClick={() => setForm({ ...form, plan: p })}
                        data-testid={`new-tenant-plan-${p}`}
                        className={`px-3 py-1 text-[10px] font-mono uppercase tracking-widest rounded-sm border transition-colors ${
                          form.plan === p ? "bg-[#B57BFF] text-black border-[#B57BFF]" : "text-[#8E9196] border-white/10 hover:text-white"
                        }`}>
                  {p}
                </button>
              ))}
            </div>
          </div>
          <Field label="Tenant admin name" value={form.admin_name} onChange={(v) => setForm({ ...form, admin_name: v })} testid="new-tenant-admin-name" placeholder="Alice Anderson" />
          <Field label="Tenant admin email" value={form.admin_email} onChange={(v) => setForm({ ...form, admin_email: v })} testid="new-tenant-admin-email" placeholder="admin@acmecorp.io" />
          <Field label="Temp password (optional — leave blank to auto-generate)" value={form.admin_password} onChange={(v) => setForm({ ...form, admin_password: v })} testid="new-tenant-admin-password" placeholder="Auto-generated if empty" />

          {/* Per-role seat caps */}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#52525B] mb-1">Seat caps — how many users of each role this tenant may create</div>
            <div className="grid grid-cols-3 gap-2">
              {["admin", "analyst", "viewer"].map((role) => (
                <div key={role}>
                  <div className="text-[9px] font-mono uppercase tracking-widest text-[#B57BFF] mb-0.5">{role}</div>
                  <input
                    type="number"
                    min="-1"
                    value={form.user_limits[role]}
                    onChange={(e) => setForm({
                      ...form,
                      user_limits: { ...form.user_limits, [role]: parseInt(e.target.value) || 0 },
                    })}
                    data-testid={`new-tenant-limit-${role}`}
                    className="w-full bg-black border border-white/10 focus:border-[#B57BFF] rounded-sm px-3 py-2 text-sm outline-none text-white font-mono"
                  />
                </div>
              ))}
            </div>
            <div className="mt-1 text-[9px] font-mono text-[#52525B]">Use <span className="text-white">-1</span> for unlimited, <span className="text-white">0</span> to block that role entirely.</div>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onCancel} className="px-3 py-2 border border-white/10 text-[10px] font-mono uppercase tracking-widest text-[#8E9196] hover:text-white rounded-sm">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="new-tenant-submit"
                  className="px-4 py-2 bg-[#B57BFF] text-black text-[10px] font-mono uppercase tracking-widest rounded-sm hover:bg-white disabled:opacity-40">
            {saving ? "Creating…" : "Create tenant"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, placeholder }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#52525B] mb-1">{label}</div>
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder={placeholder}
             className="w-full bg-black border border-white/10 focus:border-[#B57BFF] rounded-sm px-3 py-2 text-sm outline-none text-white placeholder:text-[#52525B]" />
    </div>
  );
}

function CreatedModal({ data, onClose }) {
  const admin = data.admin;
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-8" onClick={onClose}>
      <div className="bg-[#0a0a0a] border border-[#00F0FF]/60 rounded-sm p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="tenant-created-modal">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#FFC542] mb-2">⚠ Shown once — copy now</div>
        <div className="font-display font-black text-2xl mb-3">Tenant created</div>
        <div className="space-y-2 text-sm">
          <div><span className="text-[#8E9196]">Tenant:</span> {data.tenant.name} <span className="font-mono text-[#52525B]">({data.tenant.id})</span></div>
          <div><span className="text-[#8E9196]">Admin email:</span> <span className="font-mono">{admin.email}</span></div>
          <div>
            <span className="text-[#8E9196]">Temp password:</span>
            <span className="ml-2 font-mono text-[#00F0FF]" data-testid="tenant-created-password">{admin.temp_password}</span>
            <button onClick={() => { navigator.clipboard.writeText(admin.temp_password); toast.success("Copied"); }}
                    className="ml-2 text-[10px] font-mono uppercase tracking-widest text-[#8E9196] hover:text-white">Copy</button>
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-[#00F0FF] text-black text-[10px] font-mono uppercase tracking-widest rounded-sm hover:bg-white">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
